import {
  forwardRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Box,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { LayerContext } from 'state/LayerContext';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  editAnalysisSegmentName,
  removeAnalysisSegment,
  selectAnalysisSegments,
  selectHoveredSegmentIds,
  selectODUIMode,
  setAnalysisSegments,
  setHoveredSegmentIds,
} from 'state/originDestinationSlices';

import { MAX_CHARS, MAX_OD_SEGMENTS, ODUIMode } from 'appConstants';
import { pluralize } from 'appUtils';
import { InfoAlert } from 'features/common/Alerts';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import { StlButton } from 'features/common/StlButton/StlButton';
import StlHelperTxt from 'features/common/StlHelperTxt';
import { classNames, updateNameError } from 'features/common/utils';
import { MapContext } from 'features/map/MapContext';
import UndoSpan from 'features/map/UndoSpan';
import ZoomToRoute from 'features/map/overlays/ZoomToRoute';
import { getAllFeaturesFromGeoJSONSource } from 'features/map/utils/mapCommon';
import { kSelectedSegmentLayerId } from 'features/map/utils/map_constants';
import LoadingDotAnimation from 'features/spinner/LoadingDotAnimation';
import { GeoJsonProperties, Geometry } from 'geojson';

import { useUnavailableODSegments } from '../hooks/useUnavailableODSegments';

import mapStyles from '../../map/Map.module.css';
import styles from './ODMapTooltip.module.css';

type SeenSegment = {
  properties: GeoJsonProperties;
  geometry?: Geometry;
};

export default function ODMapTooltip({ viewOnly }: { viewOnly: boolean }) {
  const dispatch = useAppDispatch();
  const { refMap, mapReady } = useContext(MapContext);
  const { layer } = useContext(LayerContext);

  const segments = useAppSelector(selectAnalysisSegments);
  const hoveredSegmentIds = useAppSelector(selectHoveredSegmentIds);
  const uiMode = useAppSelector(selectODUIMode);
  const inCreateMode = uiMode === ODUIMode.Create;
  const {
    unavailableSegmentIds,
    hasUnavailableSegments,
    isValidatingSegments,
  } = useUnavailableODSegments();
  const unavailableSegmentIdSet = useMemo(
    () => new Set(unavailableSegmentIds),
    [unavailableSegmentIds],
  );

  // accumulated segments
  const [mapSegments, setMapSegments] = useState<SeenSegment[]>(undefined);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const seenRef = useRef<Record<string, SeenSegment>>({});
  const hoveredSegmentRef = useRef<HTMLDivElement | null>(null);

  const segmentNameById = useMemo(() => {
    return Object.fromEntries((segments ?? []).map((s) => [s.id, s.name]));
  }, [segments]);

  const updateSegments = () => {
    const map = refMap.current;
    if (!map) return;
    const segmentIds = segments?.map((el) => el.id) ?? [];
    const selectedIds = new Set(segmentIds);

    for (const id of Object.keys(seenRef.current)) {
      if (!selectedIds.has(String(id))) delete seenRef.current[id];
    }
    if (!segments?.length) {
      setMapSegments([]);
      return;
    }

    const allHaveGeometry = segmentIds.every(
      (seg: string) => seenRef.current[seg]?.geometry,
    );

    if (!allHaveGeometry) {
      const found = getAllFeaturesFromGeoJSONSource(
        map,
        kSelectedSegmentLayerId,
        segmentIds,
      );
      for (const seg of found) {
        seenRef.current[seg.properties.segment_id] = seg;
      }

      for (const id of segmentIds) {
        if (!seenRef.current[id]) {
          seenRef.current[id] = {
            properties: { segment_id: id },
          };
        }
      }
    }
    const allSegments = segmentIds.map((id: string) => seenRef.current[id]);
    setMapSegments(allSegments);
  };

  useEffect(() => {
    const map = refMap.current;
    if (!map) return;
    const handleSourceDataEvent = (e: mapboxgl.MapSourceDataEvent) => {
      if (e.sourceId === kSelectedSegmentLayerId) updateSegments();
    };

    map.on('sourcedata', handleSourceDataEvent);
    return () => {
      map.off('sourcedata', handleSourceDataEvent);
    };
  }, [segments, mapReady, refMap]);

  useEffect(() => {
    updateSegments();
  }, [segments, mapReady]);

  useEffect(() => {
    seenRef.current = {};
    updateSegments();
  }, [layer]);

  useEffect(() => {
    // initial reset
    dispatch(setHoveredSegmentIds(null));
  }, []);

  useEffect(() => {
    if (!isOpen || !hoveredSegmentIds?.length) return;
    hoveredSegmentRef.current?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
    });
  }, [hoveredSegmentIds, isOpen, mapSegments]);

  const handleSegmentMouseEnter = (segmentId: string) => {
    if (!hoveredSegmentIds?.includes(segmentId))
      dispatch(setHoveredSegmentIds([segmentId]));
  };

  const handleSegmentMouseLeave = () => {
    if (hoveredSegmentIds?.length > 0) dispatch(setHoveredSegmentIds(null));
  };

  const infoText =
    !viewOnly && (!segments || segments.length === 0) ? (
      <span>{`Select up to ${MAX_OD_SEGMENTS} segments`}</span>
    ) : (
      <span>{`${(segments ?? [])?.length} out of ${MAX_OD_SEGMENTS} segments selected`}</span>
    );

  const collapseId = 'od-collapse';

  const clearAllSegments = () => {
    dispatch(setAnalysisSegments(undefined));
  };

  const unavailableSegmentNames = unavailableSegmentIds.map(
    (id) => segmentNameById[id] || id,
  );

  const unavailableSegmentsWarning = hasUnavailableSegments
    ? `${unavailableSegmentIds.length} selected ${pluralize(unavailableSegmentIds.length, 'segment')} (${unavailableSegmentNames.join(', ')}) not available for the selected time period. Remove ${
        unavailableSegmentIds.length === 1 ? 'it' : 'them'
      } or choose a different month.`
    : undefined;

  return (
    <div
      className={classNames(
        mapStyles.map_tooltip_container,
        mapStyles.segments_tooltip_container,
      )}
      style={{ width: '310px' }}
    >
      <Stack className={styles.header}>
        <Typography variant="h3">
          {inCreateMode && 'Define '}Origin Destination Segments
        </Typography>
        <IconButton
          edge="end"
          disableRipple
          aria-label="Hide/Expand"
          title="Hide/Expand"
          className={styles.expand}
          aria-controls={collapseId}
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? (
            <KeyboardArrowDownIcon fontSize="medium" />
          ) : (
            <KeyboardArrowRightIcon fontSize="medium" />
          )}
        </IconButton>
      </Stack>
      <Collapse
        unmountOnExit
        id={collapseId}
        in={isOpen}
        timeout="auto"
        classes={{
          root: styles.collapse,
          wrapperInner: styles.collapseContent,
        }}
      >
        <Box display="flex" gap={0.75}>
          <InfoAlert txt={infoText} />
          {inCreateMode && (
            <StlButton
              isActive={segments?.length > 0}
              value="Reset"
              variant="outlined"
              handleClick={clearAllSegments}
              sx={{ height: '-webkit-fill-available' }}
            />
          )}
        </Box>
        {unavailableSegmentsWarning && (
          <StlHelperTxt variant="error" txt={unavailableSegmentsWarning} />
        )}
        {isValidatingSegments ? (
          <LoadingDotAnimation />
        ) : (
          <>
            {mapSegments && (
              <>
                <div className={styles.scrollableList}>
                  {mapSegments.map((el) => (
                    <SelectedSegmentBar
                      key={el.properties.segment_id}
                      ref={
                        hoveredSegmentIds?.[0] === el.properties.segment_id
                          ? hoveredSegmentRef
                          : null
                      }
                      properties={el.properties}
                      geometry={el.geometry}
                      viewOnly={viewOnly}
                      name={segmentNameById[el.properties.segment_id]}
                      unavailable={unavailableSegmentIdSet.has(
                        el.properties.segment_id,
                      )}
                      onMouseEnter={handleSegmentMouseEnter}
                      onMouseLeave={handleSegmentMouseLeave}
                    />
                  ))}
                </div>
                {!viewOnly && <UndoSpan />}
              </>
            )}
          </>
        )}
      </Collapse>
    </div>
  );
}

interface ISelectedSegmentBar {
  geometry?: Geometry;
  properties: GeoJsonProperties;
  viewOnly: boolean;
  name: string;
  unavailable?: boolean;
  onMouseEnter: (segmentId: string) => void;
  onMouseLeave: () => void;
}

const SelectedSegmentBar = forwardRef<HTMLDivElement, ISelectedSegmentBar>(
  (
    {
      geometry,
      properties,
      viewOnly,
      name,
      unavailable = false,
      onMouseEnter,
      onMouseLeave,
    },
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const segments = useAppSelector(selectAnalysisSegments);
    const [editing, setEditing] = useState<boolean>(false);
    const [editName, setEditName] = useState<string>(name);
    const hoveredSegmentIds = useAppSelector(selectHoveredSegmentIds);

    const { segment_id } = properties;

    const existingSegNames = (segments ?? [])
      .filter((s) => s.id !== segment_id)
      .map((s) => s.name?.trim().toLowerCase());

    const error =
      editing &&
      updateNameError(undefined, editName, existingSegNames, editing);

    const clearRoute = () => {
      dispatch(removeAnalysisSegment(segment_id));
    };

    const handleNameChange = () => {
      if (!editing) return;
      dispatch(editAnalysisSegmentName({ id: segment_id, name: editName }));
      setEditing(false);
    };

    const cancelNameChange = () => {
      setEditName(name);
      setEditing(false);
    };

    return (
      <>
        {error && <StlHelperTxt variant="error" txt={error} />}
        <Box
          ref={ref}
          className={classNames(
            styles.container,
            hoveredSegmentIds?.includes(segment_id) &&
              styles.containerHighlighted,
            unavailable && styles.containerUnavailable,
          )}
          title={
            unavailable ? 'Not available for the selected time period' : name
          }
          onMouseEnter={() => onMouseEnter(segment_id)}
          onMouseLeave={onMouseLeave}
        >
          <TextField
            className={styles.segmentName}
            disabled={!editing}
            value={editName}
            name="name"
            onChange={(e) => setEditName(e.target.value)}
            error={Boolean(error)}
            aria-labelledby="name"
            InputProps={{
              inputProps: { maxLength: MAX_CHARS },
            }}
          />
          <Box display="flex">
            {!editing && geometry && (
              <ZoomToRoute
                className={styles.zoomToRoute}
                geometry={geometry}
                size="12px"
              />
            )}
            {!viewOnly && (
              <>
                <IconButton
                  title="Edit name"
                  aria-label="edit"
                  onClick={() => setEditing(true)}
                  className={classNames(
                    styles.tooltipButton,
                    editing && styles.hidden,
                  )}
                >
                  <FontAwesomeIcon name="pen" size="0.75rem" />
                </IconButton>
                <IconButton
                  title="Delete"
                  aria-label="Delete selected segment"
                  onClick={clearRoute}
                  className={classNames(
                    styles.tooltipButton,
                    editing && styles.hidden,
                  )}
                >
                  <FontAwesomeIcon name="trash" size="0.75rem" />
                </IconButton>
                <IconButton
                  title="Accept"
                  aria-label="accept"
                  onClick={handleNameChange}
                  className={classNames(
                    styles.tooltipButton,
                    !editing && styles.hidden,
                  )}
                >
                  <FontAwesomeIcon name="check" size="0.75rem" />
                </IconButton>
                <IconButton
                  title="Cancel"
                  aria-label="cancel"
                  onClick={cancelNameChange}
                  className={classNames(
                    styles.tooltipButton,
                    !editing && styles.hidden,
                  )}
                >
                  <FontAwesomeIcon
                    name="xmark-large"
                    size="0.75rem"
                    type="sharp-solid"
                  />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </>
    );
  },
);
